/**
 * quiz.ts — adaptive quiz fetch/answer/hint/skip (PR-15 split of api/client.ts).
 *
 * The backend already picks bilingual text into one language based on
 * `?lang=zh|en`, so the frontend never calls pickBilingual() on quiz fields
 * — every method forwards the active UI language to the server.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Used by QuizPage + OnboardingQuizPage.
 *   - Backend: backend/src/routes/quiz.js (next/answer/hint/skip).
 *
 * 关键约束：
 *   - quizSkip logs to quiz_skips and defers the item ~3 days; UI must
 *     surface a "you can try again later" message instead of treating
 *     it as a correct answer.
 *   - answer_given supports string | number | string[] because compound
 *     fill-blank items return one element per blank.
 */
import { call } from './_core';
import type { RewardBlocked } from './games';

export interface QuizItem {
  id: number;
  type: 'multiple_choice' | 'free_text' | 'fill_blank' | 'true_false';
  question: {
    text: string;
    options?: string[];
    placeholder?: string;
    image?: string;
    pronunciation?: {
      lang: 'zh-CN' | 'en-US' | 'en-GB' | string;
      speak: string;
      pinyin?: string;
      ipa?: string;
      label?: string;
      audio_url?: string | null;
    };
    /** When > 1, the UI renders that many side-by-side input blanks (e.g. quotient + remainder). */
    blanks?: number;
    /** Bilingual labels per blank, e.g. ["商 Quotient","余数 Remainder"]. */
    blank_labels?: string[];
  };
  hint: string | null;
  difficulty: number;
  points_reward: number;
  base_points_reward?: number;
  difficulty_bonus?: number;
  topic: string;
  subject: string;
  subject_code: string;
  learning_set?: string | null;
  learning_level?: 'starter' | 'foundation' | 'builder' | 'advanced' | string | null;
}

export const quizApi = {
  // Quiz — backend picks the language based on ?lang=zh|en, so we forward
  // the active UI language on every request. Server returns text already
  // picked into the right language; the frontend no longer calls
  // pickBilingual() on quiz fields.
  quizNext(lang: 'zh' | 'en') {
    return call<{ item: QuizItem | null; message?: string }>(
      `/api/v1/child/quiz/next?lang=${lang}`,
    );
  },
  quizAnswer(
    quiz_item_id: number,
    answer_given: string | number | string[],
    time_spent_ms: number | undefined,
    lang: 'zh' | 'en',
  ) {
    return call<{
      correct: boolean; points_earned: number;
      reward_blocked?: RewardBlocked | null;
      explanation: string | null; correct_answer: string | null;
    }>(`/api/v1/child/quiz/answer?lang=${lang}`, {
      method: 'POST', body: JSON.stringify({ quiz_item_id, answer_given, time_spent_ms }),
    });
  },
  quizHint(
    quiz_item_id: number,
    answer_given: string | number | string[] | null | undefined,
    lang: 'zh' | 'en',
  ) {
    return call<{ hint: string; ai: boolean; reason?: string }>(
      `/api/v1/child/quiz/hint?lang=${lang}`,
      { method: 'POST', body: JSON.stringify({ quiz_item_id, answer_given }) },
    );
  },
  // v0.10.1 — kid taps "暂时放弃". Logs to quiz_skips, defers item 3 days.
  quizSkip(quiz_item_id: number, reason?: string) {
    return call<{ ok: true; skipped: true; deferred_for_days: number; quiz_item_id: number }>(
      '/api/v1/child/quiz/skip',
      { method: 'POST', body: JSON.stringify({ quiz_item_id, reason }) },
    );
  },
};
